<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import { useForm, usePage, Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import HostAuthLayout from '@/Layouts/HostAuthLayout.vue'

defineOptions({ layout: HostAuthLayout })

const { t } = useI18n()
const page = usePage()
const mode = ref('password')

const form = useForm({ email: '', password: '' })
const otpForm = useForm({ identifier: '' })

const flashAlert = computed(() => page.props.flash?.alert || '')
const alertMessage = ref(flashAlert.value)
const shakeNonce = ref(0)

watch(flashAlert, (msg) => {
  if (!msg) return
  alertMessage.value = msg
  nextTick(() => { shakeNonce.value++ })
})

function clearAlert() {
  if (alertMessage.value) alertMessage.value = ''
}

function submit() {
  form.transform((d) => ({ portal_account: d })).post('/login', { preserveScroll: true })
}

function otpSubmit() {
  otpForm.post('/otp', { preserveScroll: true })
}
</script>

<template>
  <h1 class="login-title">
    {{ t('host_portal.login.title') }}
  </h1>

  <div class="login-tabs">
    <button
      :class="['login-tab', mode === 'password' && 'is-active']"
      type="button"
      @click="mode = 'password'"
    >
      {{ t('host_portal.login.tabs.password') }}
    </button>
    <button
      :class="['login-tab', mode === 'code' && 'is-active']"
      type="button"
      @click="mode = 'code'"
    >
      {{ t('host_portal.login.tabs.code') }}
    </button>
  </div>

  <form
    v-if="mode === 'password'"
    :key="`pw-${shakeNonce}`"
    :class="['login-form', shakeNonce > 0 && alertMessage ? 'is-shaking' : '']"
    @submit.prevent="submit"
  >
    <label class="login-field">
      <span class="login-label">{{ t('host_portal.login.fields.email') }}</span>
      <input
        v-model="form.email"
        type="email"
        autocomplete="email"
        required
        :class="['login-input', alertMessage ? 'is-error' : '']"
        @input="clearAlert"
      >
    </label>
    <label class="login-field">
      <span class="login-label">{{ t('host_portal.login.fields.password') }}</span>
      <input
        v-model="form.password"
        type="password"
        autocomplete="current-password"
        required
        :class="['login-input', alertMessage ? 'is-error' : '']"
        @input="clearAlert"
      >
    </label>
    <button
      type="submit"
      :disabled="form.processing"
      class="login-submit"
    >
      {{ form.processing ? t('host_portal.login.submit.loading') : t('host_portal.login.submit.idle') }}
    </button>
    <Link
      href="/password/reset"
      class="login-link"
    >
      {{ t('host_portal.login.forgot') }}
    </Link>
  </form>

  <form
    v-else
    :key="`otp-${shakeNonce}`"
    :class="['login-form', shakeNonce > 0 && alertMessage ? 'is-shaking' : '']"
    @submit.prevent="otpSubmit"
  >
    <label class="login-field">
      <span class="login-label">{{ t('host_portal.otp.request.label') }}</span>
      <input
        v-model="otpForm.identifier"
        type="text"
        required
        autocomplete="off"
        :class="['login-input', alertMessage ? 'is-error' : '']"
        @input="clearAlert"
      >
    </label>
    <button
      type="submit"
      :disabled="otpForm.processing"
      class="login-submit"
    >
      {{ otpForm.processing ? t('host_portal.otp.verify.loading') : t('host_portal.otp.request.cta') }}
    </button>
    <p class="login-hint">
      {{ t('host_portal.login.code_hint') }}
    </p>
  </form>
</template>

<style scoped>
.login-title { font-size: 1.25rem; font-weight: 700; color: var(--ink); margin: 0 0 1rem; }
.login-tabs { display: flex; gap: 0.4rem; margin-bottom: 1rem; }
.login-tab {
  flex: 1; padding: 0.45rem 0.75rem;
  border: 1px solid var(--line); background: var(--surface);
  color: var(--inkSoft); border-radius: 999px; cursor: pointer; font-size: 0.875rem;
}
.login-tab.is-active { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }
.login-form { display: grid; gap: 0.875rem; }
.login-field { display: grid; gap: 0.3rem; }
.login-label { font-size: 0.8125rem; color: var(--inkSoft); }
.login-input {
  width: 100%; padding: 0.55rem 0.75rem; background: var(--surface);
  border: 1px solid var(--line); border-radius: var(--radius-input, 8px);
  color: var(--ink); font-size: 0.9375rem;
  transition: border-color 0.15s ease;
}
.login-input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.login-input.is-error { border-color: var(--danger); }
.login-submit {
  padding: 0.65rem 1rem; border: 0; border-radius: var(--radius-button, 8px);
  background: var(--accent); color: var(--accentInk); font-weight: 600;
  text-align: center; text-decoration: none; cursor: pointer; display: block;
}
.login-submit:disabled { opacity: 0.6; cursor: progress; }
.login-submit--secondary {
  background: var(--surfaceAlt); color: var(--ink); border: 1px solid var(--line);
}
.login-link { color: var(--accent); text-decoration: none; font-size: 0.875rem; text-align: center; }
.login-hint { font-size: 0.8125rem; color: var(--muted); margin: 0; text-align: center; }

@keyframes login-shake {
  0%, 100% { transform: translateX(0); }
  20%      { transform: translateX(-6px); }
  40%      { transform: translateX(6px); }
  60%      { transform: translateX(-4px); }
  80%      { transform: translateX(4px); }
}
.login-form.is-shaking { animation: login-shake 0.4s ease-in-out; }
</style>
